"use client";
import type {
	FestivalDetails,
	FetchedDbSet,
	ProcessedSet,
	Review,
} from "@workspace/types";
import { ScrollToTop } from "@workspace/ui/components/custom";
import { Tabs, TabsContent } from "@workspace/ui/components/tabs";
import { useRouter, useSearchParams } from "next/navigation";
import {
	memo,
	startTransition,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { FestivalDetailCard } from "@/components/features/festival/cards/festival-detail-card";
import { FestivalHeader } from "@/components/features/festival/festival-header";
import { FestivalTabs } from "@/components/features/festival/festival-tabs";
import { EventGroupShowsList } from "@/components/features/festival/views/event-group-shows-list";
import { FestivalFilterControls } from "@/components/features/festival/filters/festival-filter-controls";
import { FestivalModalManager } from "@/components/features/festival/modals/festival-modal-manager";
import { FestivalErrorState } from "@/components/features/festival/states/festival-error-state";
import { FestivalLoadingState } from "@/components/features/festival/states/festival-loading-state";
import {
	ArtistsTab,
	CalendarTab,
	MyListTab,
	StagesTab,
	TimeTab,
} from "@/components/features/festival/tabs";
import { useSupabase } from "@/components/providers/supabase-auth-provider";
import type { BookmarkFilters } from "@/components/shared/filters/bookmark/bookmark-filter";
import { useFestivalPageData } from "@/hooks/features/festival/data/trpc/use-festival-details";
import { trpc } from "@/lib/trpc/react";
import { useFestivalDataProcessing } from "@/hooks/features/festival/data/use-festival-data-processing";
import { useFestivalReviewManagement } from "@/hooks/features/festival/reviews/use-festival-review-management";
import { useFestivalCalendarPreferences } from "@/hooks/features/festival/ui/use-festival-calendar-preferences";
import { useFestivalFilters } from "@/hooks/features/festival/ui/use-festival-filters";
import { useRealtimeFestivalReview } from "@/hooks/features/festival/use-realtime-festival-review";
import { useRealtimeUserSetReviews } from "@/hooks/features/festival/use-realtime-user-set-reviews";
import {
	useSetSocialInteractions,
	useSocialInteractions,
	useCurrentUserDisplay,
} from "@/hooks/shared";
import { calculateOptimalCalendarDimensions } from "@/lib/features/festival/calendar/calendar-dimensions";
import { isLineupOnlyFestival } from "@/lib/features/festival/festival-status";
import {
	trackFestivalTabInteraction,
	trackFestivalView,
} from "@/lib/services/analytics";
import { getDefaultDay, isShowListEventGroup } from "@workspace/shared-utils";

// Define default state values
const DEFAULT_DAY = ""; // Will be set dynamically based on available days
const DEFAULT_BOOKMARK_FILTERS: BookmarkFilters = {
	saved: false,
	rated: false,
	following: false,
	stream: false,
};

interface FestivalPageClientProps {
	festivalSlug: string;
}

const FestivalPageClientInner = memo(function FestivalPageClientComponent({
	festivalSlug,
}: FestivalPageClientProps) {
	// Stabilize the component to prevent remounting
	const stableSlug = useMemo(() => festivalSlug, [festivalSlug]);

	const router = useRouter();
	const searchParams = useSearchParams();
	const { user } = useSupabase();
	const currentUserDisplay = useCurrentUserDisplay();

	const slug = stableSlug;

	// Use festival filters hook - memoize to prevent re-initialization
	const filterHookResult = useFestivalFilters(user);
	const {
		activeTab,
		setActiveTab,
		selectedDay,
		setSelectedDay,
		selectedStage,
		reviewFilter,
		sortAscending,
		sharedBookmarkFilters,
		myListDay,
		myListSort,
		setMyListSort,
		isHorizontalLayout,
		searchTerm,
		debouncedSearchTerm,
		selectedGenre,
		artistsSort,
		handleTabChange,
		handleDayChange,
		handleStageChange,
		handleGenreChange,
		handleReviewFilterChange,
		handleMyListDayChange,
		handleMyListSortChange,
		handleSearchChange,
		handleSortToggle,
		handleLayoutToggle,
		handleBookmarkFiltersChange,
		handleArtistsSortChange,
	} = filterHookResult;

	// Calculate isSearching state for preventing empty state flash during search
	const isSearching = useMemo(
		() => searchTerm !== debouncedSearchTerm,
		[searchTerm, debouncedSearchTerm],
	);

	// Event group state (for pre/after parties) - read from URL
	const eventGroupParam = searchParams?.get('eventGroup') ?? null;
	const urlEventGroup = eventGroupParam || 'main_festival';

	const [selectedEventGroup, setSelectedEventGroup] = useState<string>(urlEventGroup);
	const [eventGroupViewType, setEventGroupViewType] = useState<'integrated' | 'show_list'>('integrated');

	// Sync selectedEventGroup with URL when it changes
	useEffect(() => {
		setSelectedEventGroup(urlEventGroup);
	}, [urlEventGroup]);

	// Initial event type calculation from URL
	// Extract base event type from URL param (supports event_type_index format like "after_party_2")
	const initialEventType = useMemo(() => {
		// Parse URL to extract base event type
		// Supports both simple event types ("after_party") and indexed variants ("after_party_2")
		if (urlEventGroup.startsWith('after_party')) return 'after_party';
		if (urlEventGroup.startsWith('pre_party')) return 'pre_party';

		return 'main_festival';
	}, [urlEventGroup]);

	// Use optimized tRPC hook for all festival data (with event type filter)
	const {
		festival,
		user: userQuery,
		sets,
		hasError,
		error,
	} = useFestivalPageData(user?.id || "", slug, initialEventType);

	// Extract data from the combined queries
	const festivalData = festival.data as
		| {
				festival?: FestivalDetails;
				stages?: Array<{ id: string; name: string; position: number }>;
		  }
		| undefined;
	const festivalDetails = festivalData?.festival;
	const dbStagesList = festivalData?.stages || [];

	// Get available event types for the event group selector
	const { data: availableEventTypes } = trpc.festival.getAvailableEventTypes.useQuery(
		{ festivalId: festivalDetails?.id || "" },
		{ enabled: !!festivalDetails?.id }
	);

	// Determine view type from availableEventTypes data
	// Match by event_id (supports both event_type strings and UUIDs for show_list events)
	const matchedEventViewType = useMemo(() => {
		if (!availableEventTypes || !selectedEventGroup) {
			return 'integrated' as const;
		}

		// Find event by matching event_id (handles both integrated event_type strings and show_list UUIDs)
		const matchedEvent = availableEventTypes.find(et => et.event_id === selectedEventGroup);

		return matchedEvent?.view_type || 'integrated';
	}, [availableEventTypes, selectedEventGroup]);

	// Update view type when matched event changes
	useEffect(() => {
		setEventGroupViewType(matchedEventViewType);
	}, [matchedEventViewType]);

	const fetchedSetsData = useMemo(() => {
		// Always use regular sets data for better UX (no loading on tab switch)
		const data = (sets.data as unknown[]) || [];
		return data;
	}, [sets.data]);

	// Use individual loading states from the queries
	const isLoadingFestival = festival.isLoading;
	const isLoadingSets = sets.isLoading;
	const festivalError = error;

	// Stabilize IDs to prevent re-fetching
	const stableUserId = useMemo(() => user?.id, [user?.id]);
	const stableFestivalId = useMemo(
		() => festivalDetails?.id,
		[festivalDetails?.id],
	);

	// Festival review with real-time updates - now handled internally by modal
	const { review: realtimeFestivalReview } = useRealtimeFestivalReview({
		festivalId: stableFestivalId || "",
		userId: stableUserId || null,
		enabled: !!stableUserId && !!stableFestivalId,
	});

	// Use real-time for user set reviews - instant sync across tabs
	const { reviews: realtimeSetReviews, isLoading: isRealtimeLoading } = useRealtimeUserSetReviews({
		festivalId: stableFestivalId || "",
		userId: stableUserId || null,
		enabled: !!stableFestivalId && !!user,
	});

	// Use tRPC data as fast fallback while realtime is loading (tRPC has React Query cache)
	const tRPCSetReviews = useMemo(() => {
		const userData = userQuery.data as any;
		if (!userData?.setReviews) return [];
		return userData.setReviews.map((r: any) => ({
			setId: r.setId,
			artistId: r.artistId,
			rating: r.rating,
			content: r.content,
			loved: r.loved,
			sawLive: r.sawLive,
			sawStream: r.sawStream,
			wantToSee: r.wantToSee,
			mustSee: r.mustSee,
		}));
	}, [userQuery.data]);

	// Merge sources: realtime data (authoritative) wins, tRPC data (cached) is fallback
	// This ensures colors paint from tRPC cache immediately, then realtime takes over
	const userSetReviews = useMemo(() => {
		const source = realtimeSetReviews.length > 0 ? realtimeSetReviews : tRPCSetReviews;
		return source.map((r: any) => ({
			setId: r.setId,
			artistId: r.artistId,
			rating: r.rating,
			content: r.content,
			loved: r.loved,
			sawLive: r.sawLive,
			sawStream: r.sawStream,
			wantToSee: r.wantToSee,
			mustSee: r.mustSee,
		}));
	}, [realtimeSetReviews, tRPCSetReviews]);

	const [processedSets, setProcessedSets] = useState<ProcessedSet[]>([]);
	const [savedSchedule, setSavedSchedule] = useState<string[]>([]);
	const [availableDays, setAvailableDays] = useState<string[]>([]);
	const [reviewingFestival, setReviewingFestival] = useState(false);

	// Computed loading state - only show full-page loading for essential data
	const isPageReady = useMemo(() => {
		// Only check essential data loading (public festival data)
		// User data loading should not trigger full-page loading states
		const isEssentialDataLoading = isLoadingFestival || isLoadingSets;

		// Page is ready when loading is complete (regardless of whether data exists)
		// If data is null after loading, that's an error state, not a loading state
		return !isEssentialDataLoading;
	}, [isLoadingFestival, isLoadingSets]);

	// Auth modal state
	const [authModalOpen, setAuthModalOpen] = useState(false);
	const [authModalMessage, setAuthModalMessage] = useState("");

	// Sync event group state with URL changes
	useEffect(() => {
		const eventGroupParam = searchParams?.get('eventGroup') ?? null;
		const groupId = eventGroupParam || 'main_festival';

		setSelectedEventGroup(groupId);
		// Note: eventGroupViewType is controlled by API-based detection (lines 186-188)
		// to avoid race conditions and ensure database view_type is authoritative
	}, [searchParams]);

	// Handle event group change
	const handleEventGroupChange = useCallback((groupId: string, viewType: 'integrated' | 'show_list') => {
		setSelectedEventGroup(groupId);
		setEventGroupViewType(viewType);
	}, []);

	// Handle auth required
	const handleAuthRequired = useCallback(
		(message = "Sign in to track festivals and review sets") => {
			setAuthModalMessage(message);
			setAuthModalOpen(true);
		},
		[],
	);
	// Calendar dimensions
	const calendarDimensions = calculateOptimalCalendarDimensions();

	// Calendar preferences management
	const {
		calendarPreferences,
		handleResetCalendarPreferences,
		handleCalendarPreferencesSaved,
	} = useFestivalCalendarPreferences();

	// Transform tRPC reviews to Review format for review management hook
	const transformedReviews = useMemo(() => {
		if (!userSetReviews) return [];
		return userSetReviews.map((r) => ({
			setId: r.setId || "",
			artistId: r.artistId || "",
			rating: r.rating || 0,
			content: r.content,
			loved: r.loved || false,
			sawLive: r.sawLive || false,
			sawStream: r.sawStream || false,
			wantToSee: r.wantToSee || false,
			mustSee: r.mustSee || false,
		}));
	}, [userSetReviews]);

	// Use review management hook with clean camelCase data
	const {
		reviews,
		reviewingSet,
		managedSavedSchedule,
		handleToggleSetInSchedule,
		handleStartReview,
		handleCloseReviewModal,
		handleReviewSave,
	} = useFestivalReviewManagement({
		user,
		processedSets,
		userSetReviews: transformedReviews, // Pass transformed tRPC data
		savedSchedule,
		onAuthRequired: handleAuthRequired,
		setSavedSchedule,
		festivalId: festivalDetails?.id,
		// No refetch needed - single source of truth
	});

	// Social interactions hooks - must be after useFestivalReviewManagement to access reviewingSet
	const { data: socialInteractions } = useSocialInteractions(
		slug,
		user?.id || null,
	);
	const { socialInteractions: reviewModalSocialInteractions } =
		useSetSocialInteractions(slug, user?.id || null, reviewingSet || "");

	// Festival data processing
	const {
		processDbSets,
		groupSetsByDay,
		groupSetsByTimeAndDay,
		unfilteredGroupSetsByTimeAndDay,
		allGenres,
		hasNoGenreArtists,
		calendarData,
		unfilteredCalendarData,
		userInteractionCalendarData,
		userIntentCalendarData,
		artistsData,
		stagesData,
		unfilteredStagesData,
		applyReviewFilter,
		getDayCount,
	} = useFestivalDataProcessing({
		processedSets,
		setReviews: reviews, // Use managed state instead of raw tRPC data
		savedSchedule: managedSavedSchedule, // NEW: Use hook's managed schedule state
		selectedDay,
		sortAscending,
		sharedBookmarkFilters,
		searchTerm: debouncedSearchTerm,
		selectedGenre,
		reviewFilter,
		dbStagesList,
		selectedStage,
		socialInteractions,
		eventType: initialEventType, // Pass event type to filter by event group
	});

	// Process sets when data changes - batch state updates with startTransition
	useEffect(() => {
		if (festivalDetails && fetchedSetsData.length > 0) {
			// Use React 18 startTransition to batch non-urgent updates
			startTransition(() => {
				const processed = processDbSets(
					fetchedSetsData as FetchedDbSet[],
					festivalDetails,
				);
				const days = groupSetsByDay(processed, festivalDetails);

				// Batch these updates together
				setProcessedSets(processed);
				setAvailableDays(days);
			});
		}
	}, [festivalDetails, fetchedSetsData, processDbSets, groupSetsByDay]);

	// Track festival page view when festival details load
	useEffect(() => {
		if (festivalDetails) {
			trackFestivalView(festivalDetails.id, festivalDetails.name, "direct");
		}
	}, [festivalDetails]);

	// Initialize saved schedule from review data with transition
	useEffect(() => {
		if (transformedReviews && transformedReviews.length > 0) {
			startTransition(() => {
				const setsToSave = transformedReviews
					.filter((r) => r.wantToSee || r.mustSee)
					.map((r) => r.setId);
				setSavedSchedule(setsToSave);
			});
		}
	}, [transformedReviews]);

	// Handle lineup-only festival tab switching and default sort - optimized to prevent loops
	const isLineupOnly = useMemo(() => {
		return festivalDetails ? isLineupOnlyFestival(festivalDetails) : false;
	}, [festivalDetails]);

	// Handle lineup-only festival URL redirect (client-side for static generation compatibility)
	useEffect(() => {
		if (festivalDetails && isLineupOnly && !searchParams?.get("day")) {
			// Build URL with required parameters for lineup-only festivals
			const url = new URL(window.location.href);

			// Set required parameters
			url.searchParams.set("view", "artists");
			url.searchParams.set("day", "all");
			if (!url.searchParams.get("myListSort")) {
				url.searchParams.set("myListSort", "artist-asc");
			}

			// Replace URL without causing navigation
			router.replace(url.pathname + url.search, { scroll: false });
		}
	}, [festivalDetails, isLineupOnly, searchParams, router]);

	// Handle lineup-only festivals - restrict to artists/reviews tabs only
	useEffect(() => {
		if (festivalDetails && isLineupOnly) {
			// For lineup-only festivals, only allow artists and reviews tabs
			// This prevents access to calendar/time/stages tabs while allowing schedule access
			if (activeTab !== "artists" && activeTab !== "reviews") {
				startTransition(() => {
					setActiveTab("artists");
				});
			}

			// Set default sort for lineup-only festivals if not already set
			if (!searchParams?.get("myListSort")) {
				setMyListSort("artist-asc");
			}
		}
	}, [
		festivalDetails,
		isLineupOnly,
		activeTab,
		searchParams,
		setActiveTab,
		setMyListSort,
	]);

	// Set selected day when available days are loaded
	useEffect(() => {
		if (availableDays.length > 0 && !availableDays.includes(selectedDay)) {
			const newDay =
				getDefaultDay(availableDays, festivalDetails) ?? DEFAULT_DAY;
			setSelectedDay(newDay);
		}
	}, [availableDays, selectedDay, setSelectedDay, festivalDetails]);

	const currentBookmarkFilters = useMemo(() => {
		switch (activeTab) {
			case "calendar":
			case "time":
			case "stages":
			case "artists":
				return sharedBookmarkFilters || DEFAULT_BOOKMARK_FILTERS;
			case "reviews":
				return DEFAULT_BOOKMARK_FILTERS;
			default:
				return DEFAULT_BOOKMARK_FILTERS;
		}
	}, [activeTab, sharedBookmarkFilters]);

	const currentShowOnlySaved = useMemo(() => {
		const filters = currentBookmarkFilters;
		return filters ? filters.saved || filters.rated || filters.stream : false;
	}, [currentBookmarkFilters]);

	// Legacy support for showOnlySaved functionality - computed from bookmark filters
	const showOnlySavedArtists = useMemo(() => {
		const filters = currentBookmarkFilters;
		return filters.saved || filters.rated || filters.stream;
	}, [currentBookmarkFilters]);

	const handleOpenFestivalReview = useCallback(() => {
		setReviewingFestival(true);
	}, []);

	const handleCloseFestivalReview = useCallback(() => {
		setReviewingFestival(false);
	}, []);

	// Handle festival review saved (called after modal saves)
	const handleFestivalReviewSaved = useCallback(() => {
		// Modal already handles closing itself
		// Just refresh any UI that needs updating
		// Real-time hook handles the actual state updates
	}, []);

	// Wrap tab change handler to add analytics tracking
	const handleTrackedTabChange = useCallback(
		(tab: string) => {
			if (festivalDetails) {
				trackFestivalTabInteraction(
					festivalDetails.id,
					festivalDetails.name,
					tab as
						| "lineup"
						| "schedule"
						| "calendar"
						| "time"
						| "stages"
						| "reviews",
				);
			}
			handleTabChange(tab);
		},
		[festivalDetails, handleTabChange],
	);

	const filteredSetsForMyList = useMemo(() => {
		const filtered = applyReviewFilter(processedSets);
		return filtered;
	}, [applyReviewFilter, processedSets]);

	const reviewsForMyList = useMemo(() => {
		// Only include sets that have reviews - no placeholder reviews
		return filteredSetsForMyList
			.map((set) => {
				const existingReview = reviews.find((r) => r.setId === set.id);
				return existingReview; // Return the review or undefined
			})
			.filter(Boolean) as Review[]; // Remove undefined entries (safe cast since we filter them out)
	}, [filteredSetsForMyList, reviews]);

	// Memoize externalReview to prevent unnecessary re-renders of FestivalDetailCard
	const memoizedExternalReview = useMemo(() => {
		if (user && realtimeFestivalReview && festivalDetails) {
			// Transform real-time review directly to tRPC format for FestivalDetailCard
			return {
				id: realtimeFestivalReview.id,
				userId: realtimeFestivalReview.userId,
				festivalId: realtimeFestivalReview.festivalId,
				overallRating: realtimeFestivalReview.overallRating,
				vibeRating: realtimeFestivalReview.vibeRating,
				productionRating: realtimeFestivalReview.productionRating,
				logisticsRating: realtimeFestivalReview.logisticsRating,
				foodRating: realtimeFestivalReview.foodRating,
				content: realtimeFestivalReview.content,
				createdAt: realtimeFestivalReview.createdAt,
				updatedAt: realtimeFestivalReview.updatedAt,
			};
		}
		return null;
	}, [user, realtimeFestivalReview, festivalDetails]);

	const hasFestivalError = !!festivalError || hasError;
	const errorMessage = festivalError ? "Festival not found" : "";

	// Render conditionally without early returns to avoid React Hooks violation
	return (
		<>
			{!isPageReady && <FestivalLoadingState />}
			{isPageReady && (hasFestivalError || !festivalDetails) && (
				<FestivalErrorState
					hasError={hasFestivalError}
					errorMessage={errorMessage}
				/>
			)}
			{isPageReady && !hasFestivalError && festivalDetails && (
		<div className="mx-auto flex max-w-8xl flex-1 flex-col gap-3 p-3">
			<FestivalHeader
				festivalDetails={festivalDetails}
				festivalSlug={slug}
				selectedDay={selectedDay}
				availableDays={availableDays}
				calendarData={calendarData}
				processedSets={processedSets}
				reviews={reviews}
				stages={dbStagesList}
				eventType={initialEventType}
				onEventGroupChange={handleEventGroupChange}
				availableEventTypes={availableEventTypes}
				username={currentUserDisplay.data?.username ?? undefined}
				userProfileImageUrl={currentUserDisplay.data?.avatar ?? undefined}
			/>

			{/* Festival Detail Header */}
			<FestivalDetailCard
				festival={festivalDetails}
				onOpenReview={handleOpenFestivalReview}
				externalReview={memoizedExternalReview}
			/>

			<div>
				{/* Conditional rendering: Integrated view (tabs) vs Show list view */}
				{eventGroupViewType === 'integrated' ? (
				<Tabs
					key="festival-detail-tabs"
					value={activeTab}
					onValueChange={handleTrackedTabChange}
					className="w-full"
				>
					<FestivalTabs festivalDetails={festivalDetails} user={user} />

					<FestivalFilterControls
						activeTab={activeTab}
						festivalDetails={festivalDetails}
						availableDays={availableDays}
						selectedDay={selectedDay}
						handleDayChange={handleDayChange}
						getDayCount={getDayCount}
						currentBookmarkFilters={currentBookmarkFilters}
						handleBookmarkFiltersChange={handleBookmarkFiltersChange}
						isHorizontalLayout={isHorizontalLayout}
						handleLayoutToggle={handleLayoutToggle}
						sortAscending={sortAscending}
						handleSortToggle={handleSortToggle}
						processedSets={processedSets}
						isLoading={isLoadingFestival || isLoadingSets}
						selectedStage={selectedStage}
						handleStageChange={handleStageChange}
						dbStagesList={dbStagesList}
						selectedGenre={selectedGenre}
						handleGenreChange={handleGenreChange}
						allGenres={allGenres}
						hasNoGenreArtists={hasNoGenreArtists}
						searchTerm={searchTerm}
						handleSearchChange={handleSearchChange}
						artistsSort={artistsSort}
						handleArtistsSortChange={handleArtistsSortChange}
						user={user}
						myListDay={myListDay}
						handleMyListDayChange={handleMyListDayChange}
						applyReviewFilter={applyReviewFilter}
						reviewFilter={reviewFilter}
						handleReviewFilterChange={handleReviewFilterChange}
						myListSort={myListSort}
						handleMyListSortChange={handleMyListSortChange}
						calendarPreferences={calendarPreferences}
						onCalendarPreferencesSaved={handleCalendarPreferencesSaved}
					/>

					{/* Tab Content */}
					<TabsContent value="calendar" className="m-0">
						<CalendarTab
							calendarData={calendarData.filter((item) => item.day === selectedDay)}
							unfilteredCalendarData={unfilteredCalendarData}
							userInteractionCalendarData={userInteractionCalendarData}
							userIntentCalendarData={userIntentCalendarData}
							processedSets={processedSets}
							festivalDetails={festivalDetails}
							dbStagesList={dbStagesList}
							festivalSlug={slug}
							variant="public"
							authState={!!user}
							reviews={reviews}
							savedSchedule={managedSavedSchedule}
							isHorizontalLayout={isHorizontalLayout}
							sortAscending={sortAscending}
							calendarDimensions={calendarDimensions}
							calendarPreferences={calendarPreferences}
							sharedBookmarkFilters={sharedBookmarkFilters}
							selectedDay={selectedDay}
							onStartReview={handleStartReview}
							onToggleSetInSchedule={handleToggleSetInSchedule}
							onResetCalendarPreferences={handleResetCalendarPreferences}
							onClearFilters={() =>
								handleBookmarkFiltersChange({
									saved: false,
									rated: false,
									following: false,
									stream: false,
								})
							}
							onAuthRequired={handleAuthRequired}
						/>
					</TabsContent>
					<TabsContent value="time" className="m-0">
						<TimeTab
							groupSetsByTimeAndDay={groupSetsByTimeAndDay}
							unfilteredGroupSetsByTimeAndDay={unfilteredGroupSetsByTimeAndDay}
							festivalDetails={festivalDetails}
							festivalSlug={slug}
							variant="public"
							authState={!!user}
							reviews={reviews}
							savedSchedule={managedSavedSchedule}
							selectedDay={selectedDay}
							sortAscending={sortAscending}
							currentShowOnlySaved={currentShowOnlySaved}
							onClearFilters={() =>
								handleBookmarkFiltersChange({
									saved: false,
									rated: false,
									following: false,
									stream: false,
								})
							}
							onStartReview={handleStartReview}
							onToggleSetInSchedule={handleToggleSetInSchedule}
							onAuthRequired={handleAuthRequired}
						/>
					</TabsContent>
					<TabsContent value="stages" className="m-0">
						<StagesTab
							stagesData={stagesData}
							unfilteredStagesData={unfilteredStagesData}
							dbStagesList={dbStagesList}
							festivalSlug={slug}
							variant="public"
							authState={!!user}
							reviews={reviews}
							savedSchedule={managedSavedSchedule}
							selectedDay={selectedDay}
							selectedStage={selectedStage}
							currentShowOnlySaved={currentShowOnlySaved}
							onClearFilters={() =>
								handleBookmarkFiltersChange({
									saved: false,
									rated: false,
									following: false,
									stream: false,
								})
							}
							onStartReview={handleStartReview}
							onToggleSetInSchedule={handleToggleSetInSchedule}
							onStageChange={handleStageChange}
							onAuthRequired={handleAuthRequired}
						/>
					</TabsContent>
					<TabsContent value="artists" className="m-0">
						<ArtistsTab
							processedSets={artistsData}
							variant="public"
							authState={!!user}
							reviews={reviews}
							savedSchedule={managedSavedSchedule}
							bookmarkFilters={currentBookmarkFilters}
							onBookmarkFiltersChange={handleBookmarkFiltersChange}
							showOnlySavedArtists={showOnlySavedArtists}
							searchTerm={searchTerm}
							selectedGenre={selectedGenre}
							sortBy={artistsSort}
							onSearchChange={handleSearchChange}
							onGenreChange={handleGenreChange}
							onSortChange={handleArtistsSortChange}
							onStartReview={handleStartReview}
							onToggleSetInSchedule={handleToggleSetInSchedule}
							onAuthRequired={handleAuthRequired}
							isLoadingInitial={isLoadingSets}
							isSearching={isSearching}
						/>
					</TabsContent>
					{user && (
						<TabsContent value="reviews" className="m-0">
							<MyListTab
								reviews={reviewsForMyList}
								processedSets={filteredSetsForMyList}
								savedSchedule={managedSavedSchedule}
								onStartReview={handleStartReview}
								reviewFilter={reviewFilter}
								readOnly={!user}
								hideControls={true}
								externalDay={myListDay}
								externalSort={myListSort}
							/>
						</TabsContent>
					)}
				</Tabs>
				) : (
					<EventGroupShowsList
						key={`${festivalDetails?.id}-${selectedEventGroup}`}
						groupId={selectedEventGroup}
						groupName={availableEventTypes?.find(et => et.event_id === selectedEventGroup)?.event_name || 'Related Events'}
						festivalSlug={slug}
						festivalId={festivalDetails?.id || ''}
						eventType={initialEventType}
					/>
				)}
			</div>
			<FestivalModalManager
				variant="public"
				authModalOpen={authModalOpen}
				authModalMessage={authModalMessage}
				festivalSlug={slug}
				onAuthModalOpenChange={setAuthModalOpen}
				reviewingFestival={reviewingFestival}
				festival={festivalDetails}
				onCloseFestivalReview={handleCloseFestivalReview}
				onSavedFestivalReview={handleFestivalReviewSaved}
				reviewingSet={reviewingSet}
				processedSets={processedSets}
				formattedReviews={reviews}
				socialInteractions={reviewModalSocialInteractions}
				onClose={handleCloseReviewModal}
				onSave={handleReviewSave}
				user={user}
			/>

			<ScrollToTop />
		</div>
			)}
		</>
	);
});

FestivalPageClientInner.displayName = "FestivalPageClientComponent";

export const FestivalPageClient = memo(function FestivalPageClientWrapper(props: FestivalPageClientProps) {
	return (
		<Suspense fallback={<FestivalLoadingState />}>
			<FestivalPageClientInner {...props} />
		</Suspense>
	);
});
